import {
  blue,
  blueGrey,
  cyan,
  deepOrange,
  deepPurple,
  green,
  indigo,
  lightBlue,
  lightGreen,
  lime,
  pink,
  purple,
  yellow,
} from "@mui/material/colors";
import { Theme } from "@mui/material/styles";
import { WasteClassification } from "generated-graphql/graphql";

export const statusAbbreviatedLabels = {
  LARGE_QUANTITY_GENERATOR: "LQG",
  SMALL_QUANTITY_GENERATOR: "SQG",
  VERY_SMALL_QUANTITY_GENERATOR: "VSQG",
  NONE: "None",
};

export const getStatusColors = (
  theme: Theme
): Record<keyof typeof statusAbbreviatedLabels, string> => ({
  LARGE_QUANTITY_GENERATOR: indigo[600],
  SMALL_QUANTITY_GENERATOR: indigo[300],
  VERY_SMALL_QUANTITY_GENERATOR: indigo[200],
  NONE: theme.palette.grey50.main,
});

// Inactive (de-emphasized) donut slice fills. These need to read as a faint
// neutral on BOTH light and dark schemes, so they are derived from adaptive
// theme tokens rather than fixed near-white hexes.
export const getInactiveStatusColors = (
  theme: Theme
): Record<keyof typeof getStatusColors, string> => ({
  LARGE_QUANTITY_GENERATOR: theme.palette.action.selected,
  SMALL_QUANTITY_GENERATOR: theme.palette.action.hover,
  VERY_SMALL_QUANTITY_GENERATOR: theme.palette.action.hover,
  NONE: theme.palette.action.hover,
});

// Empty-state / "no data" donut fill — a faint neutral that adapts per scheme.
export const getEmptyDonutColor = (theme: Theme): string => theme.palette.action.hover;

export const getEmptyBarColor = (theme: Theme): string => theme.palette.action.hover;

export const vendorColorPalette = [deepPurple[200], purple[300], purple[200], pink[200]];

export const getVendorColor = (index: number): string => {
  return vendorColorPalette[index % vendorColorPalette.length];
};

// Shared donut chart props for consistent sizing
export const tsdfChartColors = [
  green[400],
  green[300],
  lightGreen[400],
  lightGreen[200],
  lime[400],
  lightGreen[700],
  green[200],
  lime[200],
];

export const smallDonutProps = {
  containerHeight: "11rem",
  seriesOverrides: {
    innerRadius: 35,
    outerRadius: 70,
  },
};

export const managementMethodColors = [
  blue[400],
  blue[100],
  cyan[300],
  lightBlue[700],
  blueGrey[300],
  cyan[500],
  blueGrey[500],
  blue[700],
  blue[500],
  cyan[600],
];

// Static categories that should always be shown, in the order they should
// appear
export const wasteCategories: WasteClassification[] = [
  WasteClassification.Nonhazardous,
  WasteClassification.StateRegulated,
  WasteClassification.RcraHazardous,
  WasteClassification.RcraAcutelyHazardous,
];

// Classification display mapping
export const classificationLabels: Record<WasteClassification, string> = {
  [WasteClassification.Nonhazardous]: "Non-Hazardous",
  [WasteClassification.StateRegulated]: "State Regulated",
  [WasteClassification.RcraHazardous]: "Hazardous",
  [WasteClassification.RcraAcutelyHazardous]: "Acutely Hazardous",
};

export const classificationColors: Record<WasteClassification, string> = {
  [WasteClassification.Nonhazardous]: lime[700],
  [WasteClassification.StateRegulated]: yellow[700],
  [WasteClassification.RcraHazardous]: deepOrange[300],
  [WasteClassification.RcraAcutelyHazardous]: deepOrange[900],
};

// Tooltip content for each classification
export const classificationTooltips: Record<WasteClassification, string> = {
  [WasteClassification.Nonhazardous]: "Waste that is not RCRA hazardous, or RCRA acutely hazardous",
  [WasteClassification.StateRegulated]:
    "Waste that is regulated by state laws but not classified as RCRA hazardous or acutely hazardous",
  [WasteClassification.RcraHazardous]:
    "Waste that is flammable, corrosive, reactive, or toxic (D-codes), or is listed on one of the EPA's four lists of hazardous waste (F, K, U, or P codes)",
  [WasteClassification.RcraAcutelyHazardous]:
    "Waste that has a specific code (P, or one of the F codes marked with an H)",
};
